import { useEffect } from "react";

interface SEOProps {
  title: string;
  description?: string;
  /** Absolute canonical URL for this page */
  canonical?: string;
  /** OG image absolute URL */
  ogImage?: string;
  /** Additional keywords */
  keywords?: string;
  /** noindex pages (e.g. /app, /login) */
  noIndex?: boolean;
  /** ogType — defaults to "website" */
  ogType?: string;
}

const BASE_TITLE = "Orchy — AI Workflow Orchestration Platform";
const BASE_DESCRIPTION =
  "Orchy turns a plain-language request into a live multi-agent workflow: it plans the task, runs specialist AI agents with real tools (web search, files, images, code), and shows a live execution graph, timeline, and replay as it happens — including native support for running and observing Google ADK agent runtimes.";

/** Upsert a <meta> tag by name or property attribute */
function setMeta(attr: "name" | "property", key: string, content: string) {
  let el = document.head.querySelector<HTMLMetaElement>(
    `meta[${attr}="${key}"]`
  );
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

/**
 * SEO — per-page title & meta management without react-helmet.
 *
 * Usage:
 * ```tsx
 * <SEO title="Login" description="Sign in to Orchy" noIndex />
 * ```
 */
export function SEO({
  title,
  description = BASE_DESCRIPTION,
  canonical,
  ogImage = "https://orchy.ai/og-image.png",
  keywords,
  noIndex = false,
  ogType = "website",
}: SEOProps) {
  useEffect(() => {
    // Page title
    const fullTitle = title === BASE_TITLE ? title : `${title} | Orchy`;
    document.title = fullTitle;

    // Primary meta
    setMeta("name", "description", description);
    if (keywords) setMeta("name", "keywords", keywords);
    setMeta("name", "robots", noIndex ? "noindex, nofollow" : "index, follow");

    // Open Graph
    setMeta("property", "og:title", fullTitle);
    setMeta("property", "og:description", description);
    setMeta("property", "og:type", ogType);
    setMeta("property", "og:image", ogImage);

    // Twitter Card
    setMeta("name", "twitter:title", fullTitle);
    setMeta("name", "twitter:description", description);
    setMeta("name", "twitter:image", ogImage);

    // Canonical
    let canonicalEl = document.head.querySelector<HTMLLinkElement>(
      'link[rel="canonical"]'
    );
    if (canonical) {
      if (!canonicalEl) {
        canonicalEl = document.createElement("link");
        canonicalEl.setAttribute("rel", "canonical");
        document.head.appendChild(canonicalEl);
      }
      canonicalEl.setAttribute("href", canonical);
    } else if (canonicalEl) {
      canonicalEl.remove();
    }
  }, [title, description, canonical, ogImage, keywords, noIndex, ogType]);

  return null;
}
